import { LocaleLink as Link } from '../i18n/locale-link';
import { useT } from '../i18n/locale-context';
import { COMPANY } from '../lib/company';
import { GUIDE_ON } from '../content/guide-howto';
import { countryConfig } from '../country';
import { consentRequired, openConsentSettings } from '../lib/cookie-consent';
import {
  ES_DATA_SOURCE_ATTRIBUTION,
  ES_LEGAL_LABELS,
  ES_LEGAL_PATHS,
  hasEsLegalSet,
  type EsLegalKey,
} from '../content/legal/es/paths';
import { ES_OPERATOR } from '../content/legal/es/operator';
import { ContactSupport } from './contact-support';

/** MiLicita (`es`): the Spanish legal set, the AI CRM Inc line and the PLACSP
 *  attribution replace the UZ/KZ legal links and the Uzbek operator's ИНН. */
const ES_LEGAL = hasEsLegalSet(countryConfig.code);
const ES_LEGAL_ORDER: EsLegalKey[] = ['avisoLegal', 'condiciones', 'privacidad', 'cookies'];

/** Public site footer with legal + company links. Rendered at the foot of the
 * scrollable content area in the app shell. */
export function Footer() {
  const t = useT();
  const year = new Date().getFullYear();

  const legalLinks: Array<{ to: string; label: string }> = ES_LEGAL
    ? ES_LEGAL_ORDER.map((k) => ({ to: ES_LEGAL_PATHS[k], label: ES_LEGAL_LABELS[k] }))
    : [
        { to: '/offer', label: t('footer.offer') },
        { to: '/privacy', label: t('footer.privacy') },
        { to: '/requisites', label: t('footer.requisites') },
      ];

  const links: Array<{ to: string; label: string }> = [
    // First, and sitewide on purpose. /directory is the only internal path to the
    // 31 category and 14 region hubs — 43 of those 45 were orphaned before it
    // existed (see routes/public/directory.tsx). The footer renders on every
    // prerendered page, so this one static link puts every hub two hops from
    // anywhere. It is a fixed route, not fetched data, so it cannot render empty.
    { to: '/directory', label: t('footer.directory') },
    { to: '/about', label: t('footer.about') },
    // Liciora (Chile) has no guide yet (`GUIDE_ON`).
    ...(GUIDE_ON ? [{ to: '/guide', label: t('footer.guide') }] : []),
    { to: '/plans', label: t('footer.plans') },
    ...legalLinks,
  ];

  return (
    <footer className="mt-10 border-t border-edge-light pt-6 text-[12.5px] text-ink-muted">
      <nav className="mb-3 flex flex-wrap items-center gap-x-5 gap-y-2">
        {links.map((l) => (
          <Link key={l.to} to={l.to} className="font-medium transition-colors hover:text-brand">
            {l.label}
          </Link>
        ))}
        {/* Cookie-consent deployments: the permanent way back into the cookie
            settings (AEPD guía §3.2.9 — withdrawing must be as easy as giving).
            Decided by config, so the prerendered and hydrated markup agree. */}
        {consentRequired() && (
          <button
            type="button"
            onClick={openConsentSettings}
            className="font-medium transition-colors hover:text-brand"
          >
            {t('footer.cookieSettings')}
          </button>
        )}
        {/* Support opens the contact modal (not a route), styled to match the links. */}
        <ContactSupport className="font-medium transition-colors hover:text-brand">
          {t('footer.support')}
        </ContactSupport>
      </nav>
      {ES_LEGAL ? (
        <p className="leading-[1.6]">
          © {year} {ES_OPERATOR.name} · {ES_OPERATOR.brand}
          <br />
          {ES_DATA_SOURCE_ATTRIBUTION}
        </p>
      ) : (
        <p className="leading-[1.6]">
          © {year} {COMPANY.legalName}. {t('footer.rights')}
          <br />
          {t('footer.inn')}: {COMPANY.inn}
        </p>
      )}
    </footer>
  );
}
